import React, { useState, useEffect, useRef } from 'react';
import { PatternFormat } from 'react-number-format';
import { Input } from 'legacy-components/fields/input/input';

interface SsnInputFieldProps {
  last4Ssn?: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onSsnUpdated: (isUpdated: boolean) => void;
  invalid?: boolean;
  errorMessage?: string;
  isEditSsnMode?: boolean;
  disabled?: boolean;
}

export const SsnInputField: React.FC<SsnInputFieldProps> = ({
  last4Ssn,
  value,
  onChange,
  onBlur,
  onCancel,
  onEdit,
  onSsnUpdated,
  invalid,
  errorMessage,
  isEditSsnMode,
  disabled = false,
}) => {
  const [ssnDraft, setSsnDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const maskedSsn = last4Ssn ? `***-**-${last4Ssn}` : '***-**-****';

  useEffect(() => {
    if ((!value || value.length === 0) && !isEditSsnMode) {
      setSsnDraft('');
    }
  }, [value, last4Ssn]);

  const handleEditClick = () => {
    setSsnDraft('');
    onChange('');
    onSsnUpdated(false);
    onEdit?.();
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleCancel = () => {
    setSsnDraft('');
    onChange('');
    onSsnUpdated(false);
    onCancel?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = e.target.value.replaceAll('-', '').replaceAll('_', '');
    setSsnDraft(numValue);
    onChange(numValue);
    
    numValue.length === 9 ? onSsnUpdated(true) : onSsnUpdated(false);
  };

  const handleBlur = () => {
    if (ssnDraft.length > 0 && ssnDraft.length !== 9) {
      onSsnUpdated(false);
    } else if (ssnDraft.length === 9) {
      if (ssnDraft === '000000000' || ssnDraft.startsWith('000')) {
        onSsnUpdated(false);
      } else {
        onSsnUpdated(true);
      }
    }
    
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!isEditSsnMode && last4Ssn) {
    return (
      <div className="relative">
        <div className="flex items-center h-[40px] px-3 border border-neutral-200 rounded-lg bg-white">
          <span className="flex-1 text-sm text-gray-700">{maskedSsn}</span>
          {!disabled && (
            <button
              type="button"
              onClick={handleEditClick}
              className="text-[#66CCFF] text-sm font-medium hover:underline focus:outline-none"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="relative">
        <PatternFormat
          name="socialSecurityNumber"
          format="###-##-####"
          placeholder="999-99-9999"
          value={ssnDraft}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          invalid={invalid}
          customInput={Input}
          valueIsNumericString
          mask="_"
          disabled={disabled}
          inputRef={inputRef}
        />
        {last4Ssn && <button
          type="button"
          onClick={handleCancel}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#66CCFF] text-sm font-medium hover:underline focus:outline-none"
        >
          Cancel
        </button>}
      </div>
      {(errorMessage) && (
        <span className="text-xs text-error py-1">{errorMessage}</span>
      )}
    </div>
  );
};
